import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function toCsv(rows: Array<Record<string, any>>, headers?: string[]): string {
  if (!rows || rows.length === 0) return '';
  const cols = headers && headers.length > 0 ? headers : Array.from(new Set(rows.flatMap(r => Object.keys(r))));
  const esc = (v: any) => {
    if (v === null || v === undefined) return '';
    const s = String(v).replace(/\r?\n/g, ' ');
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [cols.join(',')];
  for (const r of rows) lines.push(cols.map(c => esc(r[c])).join(','));
  return lines.join('\n');
}

export function downloadCsv(filename: string, csvContent: string) {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Format a number to a fixed number of decimal places, rounded properly.
 * Prevents JavaScript float precision artifacts like 0.000000000001164
 * @param value  The raw number
 * @param digits Number of decimal places (default 2)
 */
export function fmtNum(value: number | string | null | undefined, digits = 2): string {
  const n = Number(value ?? 0);
  if (!isFinite(n)) return (0).toFixed(digits);
  return n.toLocaleString('en-IN', { minimumFractionDigits: digits, maximumFractionDigits: digits });
}

/**
 * Format a number as an Indian currency string (₹x,xx,xxx.xx)
 * @param value  The raw number
 * @param digits Number of decimal places (default 2)
 */
export function fmtCurrency(value: number | string | null | undefined, digits = 2): string {
  return '₹' + fmtNum(value, digits);
}

/**
 * Round a raw number to N decimal places to strip float precision errors.
 * Use this when storing computed values, not for display.
 * @param value  The raw number
 * @param digits Number of decimal places (default 3)
 */
export function roundNum(value: number | string | null | undefined, digits = 3): number {
  const n = Number(value ?? 0);
  if (!isFinite(n)) return 0;
  return Number(n.toFixed(digits));
}

